<template>
  <div class="student-profile">
    <el-card shadow="never">
      <div class="config-section">
        <el-form inline>
          <el-form-item label="学号">
            <el-input v-model="filters.studentNo" placeholder="请输入学号" style="width: 150px" clearable />
          </el-form-item>
          <el-form-item label="雷达图考试(必填)">
            <el-select v-model="filters.examType" placeholder="选择考试" style="width: 140px" allow-create filterable>
              <el-option-group label="常规考试">
                  <el-option label="月考1" value="monthly_1" />
                  <el-option label="期中考试" value="midterm" />
                  <el-option label="月考2" value="monthly_2" />
                  <el-option label="期末考试" value="final" />
              </el-option-group>
              <el-option-group label="高三模拟">
                  <el-option label="一模" value="mock_1" />
                  <el-option label="二模" value="mock_2" />
                  <el-option label="三模" value="mock_3" />
              </el-option-group>
              <el-option-group label="自定义考试" v-if="dynamicExamTypes.length > 0">
                  <el-option v-for="type in dynamicExamTypes" :key="type" :label="type" :value="type" />
              </el-option-group>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :icon="DataAnalysis" @click="fetchData" :loading="loading" :disabled="!semesterId || !filters.studentNo || !filters.examType">
              诊断画像
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <div v-if="loading" style="padding: 40px; text-align: center;">
        <el-skeleton :rows="8" animated />
      </div>
      <el-empty v-else-if="!hasData" description="暂无该学生的诊断数据，请检查学号是否正确" />
      <div v-else class="charts-container">
        
        <!-- 趋势图 -->
        <div class="chart-box">
          <h3 class="chart-title">历次考试年级排名趋势</h3>
          <div class="chart-wrapper">
            <v-chart class="chart" :option="trendChartOption" autoresize />
          </div>
        </div>

        <!-- 雷达图 -->
        <div class="chart-box">
          <h3 class="chart-title">单次考试学科均衡诊断 ({{ filters.examType }})</h3>
          <p class="chart-desc">1.0表示年级平均水平。大于1.0为优势学科，小于1.0为弱势学科。</p>
          <div class="chart-wrapper">
            <v-chart class="chart" :option="radarChartOption" autoresize />
          </div>
        </div>

      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, defineProps } from 'vue'
import VChart from 'vue-echarts'
import '@/utils/echarts'
import { DataAnalysis } from '@element-plus/icons-vue'
import { gradeAnalysisApi } from '@/api'
import { useExamTypes } from '@/utils/useExamTypes'
import { ElMessage } from 'element-plus'

const props = defineProps({
  semesterId: { type: Number, required: false },
  subjectConfigs: { type: Array, required: true }
})

const loading = ref(false)
const filters = reactive({
  studentNo: '',
  examType: ''
})

// 动态考试类型
const { dynamicExamTypes } = useExamTypes(computed(() => props.semesterId))

const trendData = ref([])
const radarData = ref([])

const hasData = computed(() => trendData.value.length > 0 || radarData.value.length > 0)

const examTypeMap = {
  'monthly_1': '月考1', 'midterm': '期中', 'monthly_2': '月考2', 'final': '期末',
  'mock_1': '一模', 'mock_2': '二模', 'mock_3': '三模'
}

// 排名越小越好，所以在图表中反转y轴
const trendChartOption = computed(() => {
  if (trendData.value.length === 0) return {}
  
  const exams = trendData.value.map(d => examTypeMap[d.exam_type] || d.exam_type)
  const gradeRanks = trendData.value.map(d => d.grade_rank)
  const classRanks = trendData.value.map(d => d.class_rank)

  return {
    tooltip: { trigger: 'axis' },
    legend: { data: ['年级排名', '班级排名'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: exams },
    yAxis: { type: 'value', inverse: true, min: 1 }, // 越靠上名次越好(1名)
    series: [
      {
        name: '年级排名',
        type: 'line',
        data: gradeRanks,
        markPoint: { data: [{ type: 'min', name: '最高排名' }] },
        itemStyle: { color: '#4f46e5' },
        lineStyle: { width: 3 }
      },
      {
        name: '班级排名',
        type: 'line',
        data: classRanks,
        itemStyle: { color: '#10b981' },
        lineStyle: { width: 3, type: 'dashed' }
      }
    ]
  }
})

const radarChartOption = computed(() => {
  if (radarData.value.length === 0) return {}

  const indicator = radarData.value.map(d => ({ name: d.subject, max: 2.0 })) // 相对均分，最高设为2.0倍
  const values = radarData.value.map(d => d.ratio)

  return {
    tooltip: {
      formatter: function(params) {
        let html = `<strong>${params.name}</strong><br/>`;
        radarData.value.forEach(d => {
          html += `${d.subject}得分: ${d.score} (均分${d.grade_avg})<br/>`
        })
        return html;
      }
    },
    radar: {
      indicator: indicator,
      radius: '65%',
      splitNumber: 4,
      axisName: { color: '#374151', fontWeight: 'bold' },
      splitArea: {
        areaStyle: { color: ['rgba(79, 70, 229, 0.05)', 'rgba(79, 70, 229, 0.1)', 'rgba(79, 70, 229, 0.15)', 'rgba(79, 70, 229, 0.2)'] }
      }
    },
    series: [
      {
        name: '学科诊断',
        type: 'radar',
        data: [
          {
            value: values,
            name: '相对得分率',
            areaStyle: { color: 'rgba(79, 70, 229, 0.4)' },
            lineStyle: { color: '#4f46e5', width: 2 },
            itemStyle: { color: '#4f46e5' }
          }
        ]
      }
    ]
  }
})

const fetchData = async () => {
  if (!props.semesterId || !filters.studentNo || !filters.examType) return

  loading.value = true
  try {
    // 并行请求两个接口
    const [trendRes, radarRes] = await Promise.all([
      gradeAnalysisApi.getStudentTrend({
        semester_id: props.semesterId,
        student_no: filters.studentNo,
        subject_configs: props.subjectConfigs
      }).catch(() => ({ trend: [] })),
      
      gradeAnalysisApi.getStudentRadar({
        semester_id: props.semesterId,
        exam_type: filters.examType,
        student_no: filters.studentNo
      }).catch(() => ({ radar: [] }))
    ])
    
    trendData.value = trendRes.trend || []
    radarData.value = radarRes.radar || []
    
    if (!hasData.value) {
      ElMessage.warning('未能获取到该学生的分析数据')
    }
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.config-section {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
}

.charts-container {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.chart-box {
  flex: 1;
  min-width: 400px;
  background: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
}

.chart-title {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 16px;
  color: #111827;
}

.chart-desc {
  font-size: 13px;
  color: #6b7280;
  margin-top: 0;
  margin-bottom: 16px;
}

.chart-wrapper {
  height: 400px;
  width: 100%;
}

.chart {
  width: 100%;
  height: 100%;
}
</style>
